/**
 * useUrlState (TMMPDM-0035, NAV-008 / NAV-009 / NAV-010) — one typed binding between a page's view
 * state (search, filters, status, period, page, selected card) and its URL search params, so
 * Back/Forward, a deep link and a reload all restore the view.
 *
 * - A value equal to its default is omitted from the URL (clean links); a missing, unknown or
 *   invalid value reads as the default — a hand-edited URL never crashes a page.
 * - Discrete choices push a history entry (the default); a search box's debounced commit passes
 *   `history: 'replace'` so Back never walks keystrokes.
 * - Changing any key other than `page` resets `page` in the same update.
 * - Keys outside the schema (a detail page's `?tab=`) are kept, and so is `location.state` (the
 *   list a record was opened from — `useReturnTo`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

type UrlValue = string | number | undefined;

export type UrlSchema<T extends Record<string, UrlValue>> = {
  [K in keyof T]: { default: T[K]; parse: (raw: string) => T[K] | undefined };
};

export interface UrlStateOptions {
  history?: 'push' | 'replace';
}

export type SetUrlState<T> = (patch: Partial<T>, opts?: UrlStateOptions) => void;

/** The page-number key a filter change resets. */
const PAGE_KEY = 'page';

export function useUrlState<T extends Record<string, UrlValue>>(
  schema: UrlSchema<T>,
): [T, SetUrlState<T>] {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const search = searchParams.toString();

  const state = useMemo(() => {
    const params = new URLSearchParams(search);
    const next: Record<string, UrlValue> = {};
    for (const key of Object.keys(schema) as (keyof T & string)[]) {
      const field = schema[key];
      const raw = params.get(key);
      next[key] = raw === null ? field.default : (field.parse(raw) ?? field.default);
    }
    return next as T;
  }, [schema, search]);

  const setState = useCallback<SetUrlState<T>>(
    (patch, opts) => {
      const keys = Object.keys(patch) as (keyof T & string)[];
      const resetsPage =
        PAGE_KEY in schema && !(PAGE_KEY in patch) && keys.some((key) => key !== PAGE_KEY);
      const params = new URLSearchParams(search);
      const write = (key: keyof T & string, value: UrlValue) => {
        if (value === undefined || value === '' || value === schema[key].default) {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      };
      for (const key of keys) write(key, patch[key]);
      if (resetsPage) params.delete(PAGE_KEY);
      if (params.toString() === search) return;
      setSearchParams(params, {
        replace: opts?.history === 'replace',
        state: location.state as unknown,
      });
    },
    [location.state, schema, search, setSearchParams],
  );

  return [state, setState];
}

/** Parses one of a fixed set of values; anything else is the default. */
export function oneOf<V extends string>(values: readonly V[]): (raw: string) => V | undefined {
  return (raw) => ((values as readonly string[]).includes(raw) ? (raw as V) : undefined);
}

/** A positive whole number (an id, a 1-based page); anything else is the default. */
export function positiveInt(raw: string): number | undefined {
  if (!/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
}

/** Any non-blank text (a search, or a status the server defines). */
export function text(raw: string): string | undefined {
  const value = raw.trim();
  return value === '' ? undefined : value;
}

/** An ISO calendar date `YYYY-MM-DD` that exists (TMMPDM-0044: `2026-13-45` is the default). */
export function isoDate(raw: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? raw
    : undefined;
}

/**
 * The search box over a URL-held query: what the field shows while typing, committed to the URL
 * after the existing 250 ms debounce (the caller commits with `history: 'replace'`). The field keeps
 * the text as typed (a trailing space survives the commit); only the committed query is trimmed.
 * Whenever the URL's query changes some other way — Back, Clear filters — the field shows it.
 *
 * TMMPDM-0044: under the data router the URL update is a transition, so a commit reaches the URL a
 * render or more after it is made. The draft remembers the value in flight (`target`) and keeps
 * showing until the URL carries it; only then does it re-base on it — no flash of the old query,
 * no dropped keystroke.
 */
export function useSearchDraft(
  query: string,
  commit: (value: string) => void,
  delayMs = 250,
): { value: string; onChange: (value: string) => void; discard: () => void } {
  const [draft, setDraft] = useState<{
    /** The query the text was typed over. */
    base: string;
    text: string;
    /** A committed value the URL doesn't carry yet. */
    target: string | null;
  } | null>(null);
  // The URL now carries the committed value: re-base on it (adjusting state while rendering).
  if (draft !== null && draft.target !== null && draft.target === query) {
    setDraft({ base: query, text: draft.text, target: null });
  }
  const pending = draft !== null && (draft.base === query || draft.target === query) ? draft : null;
  // The latest commit, so a caller's inline callback doesn't restart the debounce every render.
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  });

  useEffect(() => {
    if (pending === null) return;
    const next = pending.text.trim();
    // What the URL carries, or will once the commit in flight lands.
    if (next === (pending.target ?? query)) return;
    const timer = window.setTimeout(() => {
      // Keeps the typed text in the field while the URL catches up.
      setDraft({ base: query, text: pending.text, target: next });
      commitRef.current(next);
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, pending, query]);

  return {
    value: pending ? pending.text : query,
    onChange: (value) => setDraft({ base: query, text: value, target: pending?.target ?? null }),
    discard: () => setDraft(null),
  };
}
